import React, { ReactElement, useEffect } from "react";

import { Control, FieldErrors } from "react-hook-form";

import { Grid } from "@mui/material";
import { Select, DateField, NumericField } from "core";

import { EMPTY_STRING, yesNoOptions } from "shared/constants";
import { FillFormOptionType } from "master-data/enums";
import { Current, IRevisionEntriesInput } from "../SupplierAgreements/types";
import { vatOptions } from "../SupplierAgreements/constants";

export interface FormSupplierAgreementsEntryProps {
  control: Control;
  current: Current;
  errors?: FieldErrors;
  isEdit: boolean;
  revisionValues?: IRevisionEntriesInput;
  /**
   * Fill form option type
   */
  fillOption?: FillFormOptionType;
  /**
   * setValues
   */
  setValue: any;
  /**
   * resetFillOption
   */
  resetFillOption: (state: FillFormOptionType | undefined) => void;
  /*
   * onDialogSave
   */
  onDialogSave: () => void;
}

export const FormSupplierAgreementsEntry = ({
  control,
  current,
  errors = {},
  isEdit,
  revisionValues,
  fillOption,
  setValue,
  resetFillOption,
  onDialogSave,
}: FormSupplierAgreementsEntryProps): ReactElement => {
  const {
    rowIndex,
    percentageReceived,
    paidWithinNumberOfDays,
    startDate,
    deductedFromPayment,
    isVATIncluded,
  } = current ?? {};

  // #region Effects
  useEffect(() => {
    if (fillOption) {
      setValue(`entries.${rowIndex}.percentageReceived`, 0);
      setValue(`entries.${rowIndex}.paidWithinNumberOfDays`, 0);
      setValue(`entries.${rowIndex}.startDate`, new Date());
      setValue(`entries.${rowIndex}.deductedFromPayment`, 1);
      setValue(`entries.${rowIndex}.isVATIncluded`, 1);
    }
    return () => {
      if (
        fillOption === FillFormOptionType.fillAllAndSave ||
        fillOption === FillFormOptionType.fillMandatoryAndSave
      ) {
        onDialogSave();
      }
      resetFillOption(undefined);
    };
  }, [
    fillOption,
    setValue,
    rowIndex,
    percentageReceived,
    paidWithinNumberOfDays,
    startDate,
    deductedFromPayment,
    isVATIncluded,
    resetFillOption,
    onDialogSave,
  ]);
  // #endregion Effects

  return (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <NumericField
          id="percentageReceived"
          name={`entries.${rowIndex}.percentageReceived`}
          label="Percentage Received *"
          fullWidth
          defaultValue={percentageReceived ?? EMPTY_STRING}
          error={!!errors.percentageReceived?.message}
          helperText={errors.percentageReceived?.message}
          revisionValue={revisionValues?.PercentageReceived}
          control={control}
          percentage
          disabled={!isEdit}
        />
      </Grid>
      <Grid item xs={6}>
        <NumericField
          id="paidWithinNumberOfDays"
          name={`entries.${rowIndex}.paidWithinNumberOfDays`}
          label="Paid Within Number Of Days *"
          fullWidth
          integer
          defaultValue={paidWithinNumberOfDays ?? EMPTY_STRING}
          error={!!errors.paidWithinNumberOfDays?.message}
          helperText={errors.paidWithinNumberOfDays?.message}
          revisionValue={revisionValues?.PaidWithinNumberOfDays}
          control={control}
          disabled={!isEdit}
        />
      </Grid>
      <Grid item xs={6}>
        <DateField
          fullWidth
          name={`entries.${rowIndex}.startDate`}
          label="Start Date *"
          defaultValue={startDate}
          error={!!errors.startDate?.message}
          helperText={errors.startDate?.message}
          revisionValue={revisionValues?.StartDate}
          control={control}
          disabled={!isEdit}
        />
      </Grid>
      <Grid item xs={6}>
        <Select
          id="deductedFromPayment"
          name={`entries.${rowIndex}.deductedFromPayment`}
          label="Deducted From Payment *"
          variant="outlined"
          fullWidth
          defaultValue={deductedFromPayment ?? EMPTY_STRING}
          control={control}
          options={yesNoOptions}
          excludeDefault
          error={!!errors.deductedFromPayment?.message}
          helperText={errors.deductedFromPayment?.message}
          revisionValue={revisionValues?.DeductedFromPayment}
          disabled={!isEdit}
        />
      </Grid>
      <Grid item xs={6}>
        <Select
          id="isVATIncluded"
          name={`entries.${rowIndex}.isVATIncluded`}
          label="VAT *"
          variant="outlined"
          fullWidth
          defaultValue={isVATIncluded ?? EMPTY_STRING}
          control={control}
          options={vatOptions}
          excludeDefault
          error={!!errors.isVATIncluded?.message}
          helperText={errors.isVATIncluded?.message}
          revisionValue={revisionValues?.IsVATIncluded}
          disabled={!isEdit}
        />
      </Grid>
    </Grid>
  );
};

export default FormSupplierAgreementsEntry;
